/**
 * Stockfish 18 typed UCI engine layer (Workstream B2–B6).
 *
 * Replaces the old custom `stockfish.wasm@0.10.0` Worker that emitted
 * non-standard UCI fields (`baseTurn`, `pvSan`, `bmc`). This layer drives a
 * *stock* SF18 build (the `stockfish` npm package) which speaks STANDARD UCI
 * only, and converts the UCI principal variation to SAN client-side via
 * chess.js (see parser.ts).
 *
 * Responsibilities:
 *  - init(): create Worker, run the real `uci`/`uciok` + `isready`/`readyok`
 *    handshake (the old code never waited), with onerror + load-failure
 *    handling and an init timeout.
 *  - analyze(fen, opts): tag each search with a monotonic token so stale
 *    `info`/`bestmove` lines from a previous position can be discarded (B4).
 *  - setOption / newGame / stop / destroy.
 *  - Worker crash -> graceful surfacing via onError so the React layer can
 *    reinit (B6); unsupported browser -> init() rejects, caller no-ops.
 *
 * THREADING: single-threaded ONLY. The in-browser SF18 study
 * (2026-06-16-in-browser-sf18-vs-sf16-FINDINGS.md §8) found multi-threaded
 * WASM is actively HARMFUL at the interactive ≤1 s/move regime (~5x slower at
 * fixed depth, equal-or-shallower depth at a fixed time budget). The
 * COOP/COEP / SharedArrayBuffer multi-thread path has therefore been removed;
 * we always load the single-threaded SF18-lite build.
 */

export interface AnalyzeOptions {
  /** Search depth (e.g. PlayChess uses 32). */
  depth?: number;
  /** Number of principal variations (UCI MultiPV). */
  multiPV?: number;
  /** Optional hard time cap in ms (UCI `movetime`). */
  movetime?: number;
}

/** A single raw UCI `info` line plus the search token it belongs to. */
export interface EngineInfoLine {
  /** The full raw UCI line, forwarded verbatim to onResult's `raw` arg. */
  raw: string;
  /** Search token that produced this line; used by callers to drop stale data. */
  token: number;
}

export interface EngineCallbacks {
  /** Fired for every standard-UCI `info ... pv ...` line of the CURRENT search. */
  onInfo?: (line: EngineInfoLine) => void;
  /** Fired on `bestmove` for the current search. */
  onBestMove?: (bestMove: string, token: number) => void;
  /** Fired on worker error / crash / load failure so the caller can reinit. */
  onError?: (err: Error) => void;
}

/**
 * Where the SF18 Worker script + WASM live once a human copies them in
 * (see B1 install notes). We point the Worker at a small bootstrap script
 * served from the static `web/` root, matching the existing `/stockfish.js`
 * serving path. The bootstrap (web/stockfish.js) loads the single-threaded
 * SF18-lite WASM variant.
 */
const SINGLE_THREADED_WORKER = '/stockfish.js';

const INIT_TIMEOUT_MS = 15000;

// Stable top-of-file marker emitted by web/stockfish.js. Used ONLY as a soft
// confirmation hint (a missing marker is logged, never fatal) so editing the
// bootstrap header can't silently disable the in-browser path.
const STOCKFISH_BOOTSTRAP_SIGNATURE = 'Stockfish 18 Worker bootstrap';

// Bound the validation fetch so a stalled network can't hang init() (the
// INIT_TIMEOUT_MS timer only covers the handshake, which starts after this).
const ASSET_PROBE_TIMEOUT_MS = 6000;

/** Positive detection of an HTML page served where JS was expected (the SPA
 * fallback case — the original production failure). */
function looksLikeHtml(contentType: string, body: string): boolean {
  if (contentType.includes('text/html')) {
    return true;
  }
  const head = body.trimStart().slice(0, 64).toLowerCase();
  return (
    head.startsWith('<!doctype') ||
    head.startsWith('<html') ||
    head.startsWith('<')
  );
}

function isFatalStockfishWorkerLine(line: string): boolean {
  return (
    line.startsWith('info string bootstrap load failed:') ||
    line.startsWith('info string inner worker error:')
  );
}

function stockfishWorkerLineToError(line: string): Error {
  return new Error(`Stockfish worker bootstrap failed: ${line}`);
}

async function assertWorkerBootstrapAsset(workerUrl: string): Promise<void> {
  if (typeof fetch !== 'function') {
    return;
  }

  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), ASSET_PROBE_TIMEOUT_MS)
    : null;

  let response: Response;
  try {
    response = await fetch(workerUrl, {
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined,
    });
  } catch {
    // Couldn't validate — network error, CSP `connect-src`, or probe timeout.
    // This is NOT proof the asset is bad, so don't block: let `new Worker` be
    // the real arbiter (its onerror still catches a genuinely broken asset).
    // Blocking here would false-disable the browser path on environments that
    // permit workers but not fetch.
    return;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }

  const contentType = response.headers.get('content-type') || '';
  const body = await response
    .clone()
    .text()
    .then(
      text => text,
      () => '',
    );

  // Only block on a POSITIVE signal that the wrong thing was served: a 404/5xx,
  // or an HTML SPA-fallback page instead of the JS bootstrap (the original
  // production bug). A missing signature is treated as a soft warning — never
  // fatal — so editing the bootstrap header can't silently kill the path.
  if (!response.ok || looksLikeHtml(contentType, body)) {
    throw new Error(
      `Invalid Stockfish worker asset at ${workerUrl}: status=${
        response.status
      } contentType=${contentType || 'unknown'} bodyStart=${JSON.stringify(
        body.slice(0, 80),
      )}`,
    );
  }

  if (!body.includes(STOCKFISH_BOOTSTRAP_SIGNATURE)) {
    console.warn(
      `[engine] ${workerUrl} loaded but bootstrap signature missing — ` +
        'proceeding (Worker will arbitrate). Update the signature if the bootstrap changed.',
    );
  }
}

/** True when the current environment can host a Web Worker at all. */
export function isEngineSupported(): boolean {
  return typeof Worker !== 'undefined';
}

type Resolver = () => void;

export class StockfishEngineClient {
  private worker: Worker | null = null;
  private callbacks: EngineCallbacks;
  private ready = false;

  /**
   * Monotonic search token. Incremented on every analyze()/stop(). Only
   * `info`/`bestmove` lines tagged with the latest token are forwarded; this
   * is how stale results from a previous FEN are discarded (B4).
   */
  private searchToken = 0;

  // Handshake awaiters.
  private uciOkResolve: Resolver | null = null;
  private readyOkResolve: Resolver | null = null;

  constructor(callbacks: EngineCallbacks = {}) {
    this.callbacks = callbacks;
  }

  /**
   * Replace the callback set after construction. Used by batch consumers
   * (e.g. browser game analysis) that re-target onInfo/onBestMove per search.
   */
  setCallbacks(callbacks: EngineCallbacks): void {
    this.callbacks = callbacks;
  }

  get currentToken(): number {
    return this.searchToken;
  }

  /**
   * Create the Worker and run the full UCI handshake:
   *   uci      -> await uciok
   *   isready  -> await readyok
   * Rejects on unsupported browser, worker load failure, or handshake timeout.
   */
  async init(): Promise<void> {
    if (!isEngineSupported()) {
      throw new Error('Web Workers unavailable: Stockfish engine unsupported');
    }

    const workerUrl = SINGLE_THREADED_WORKER;
    await assertWorkerBootstrapAsset(workerUrl);

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) {
          return;
        }
        settled = true;
        this.cleanupWorker();
        reject(new Error('Stockfish init timed out'));
      }, INIT_TIMEOUT_MS);

      let worker: Worker;
      try {
        worker = new Worker(workerUrl);
      } catch (e) {
        clearTimeout(timer);
        reject(
          e instanceof Error ? e : new Error('Failed to construct Worker'),
        );
        return;
      }
      this.worker = worker;

      // Load failure / runtime crash during init.
      worker.onerror = event => {
        const err = new Error(
          `Stockfish worker error: ${[
            event.message || 'unknown',
            event.filename || '',
            event.lineno ? `line ${event.lineno}` : '',
            event.colno ? `col ${event.colno}` : '',
          ]
            .filter(Boolean)
            .join(' ')}`,
        );
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          this.cleanupWorker();
          reject(err);
        } else {
          // Crash after init -> surface for reinit (B6).
          this.ready = false;
          this.callbacks.onError?.(err);
        }
      };

      worker.onmessage = (event: MessageEvent) => {
        const line = typeof event.data === 'string' ? event.data : '';
        if (isFatalStockfishWorkerLine(line)) {
          const err = stockfishWorkerLineToError(line);
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            this.cleanupWorker();
            reject(err);
          } else {
            this.ready = false;
            this.callbacks.onError?.(err);
          }
          return;
        }
        this.handleMessage(line);
      };

      // Drive the handshake. uciok then readyok then resolve.
      this.uciOkResolve = () => {
        this.send('isready');
      };
      this.readyOkResolve = () => {
        if (settled) {
          return;
        }
        settled = true;
        clearTimeout(timer);
        this.ready = true;
        resolve();
      };

      // Tell the WASM bootstrap to load the single-threaded variant
      // (used by web/stockfish.js). Multi-threading is intentionally removed
      // (see header note + FINDINGS §8).
      this.send('cc:threads single');
      this.send('uci');
    });
  }

  /** Route a raw UCI line from the worker to the right handler. */
  private handleMessage(line: string): void {
    if (!line) {
      return;
    }

    if (line === 'uciok' || line.startsWith('uciok')) {
      this.uciOkResolve?.();
      return;
    }
    if (line === 'readyok' || line.startsWith('readyok')) {
      this.readyOkResolve?.();
      return;
    }

    if (line.startsWith('info') && line.includes(' pv ')) {
      this.callbacks.onInfo?.({raw: line, token: this.searchToken});
      return;
    }

    if (line.startsWith('bestmove')) {
      const parts = line.split(/\s+/);
      const bestMove = parts[1] ?? '';
      this.callbacks.onBestMove?.(bestMove, this.searchToken);
      return;
    }
  }

  /** Post a single UCI command to the worker. */
  private send(cmd: string): void {
    this.worker?.postMessage(cmd);
  }

  setOption(name: string, value: string | number): void {
    this.send(`setoption name ${name} value ${value}`);
  }

  newGame(): void {
    this.send('ucinewgame');
  }

  /**
   * Stop the current search. Bumps the search token so any late `info`/
   * `bestmove` lines from the stopped search are ignored (B4). Returns the
   * new token so the caller can correlate the next analyze().
   */
  stop(): number {
    this.searchToken += 1;
    this.send('stop');
    return this.searchToken;
  }

  /**
   * Start a new analysis on `fen`. Bumps the search token first so this search
   * owns all subsequent info/bestmove lines. Caller is responsible for having
   * stopped any prior search and (ideally) awaiting its ack before calling.
   *
   * Returns the search token tagged on every line this search produces.
   */
  analyze(fen: string, opts: AnalyzeOptions = {}): number {
    if (!this.ready || !this.worker) {
      throw new Error('Stockfish engine not ready');
    }
    this.searchToken += 1;
    const token = this.searchToken;

    const {depth, multiPV, movetime} = opts;
    if (typeof multiPV === 'number') {
      this.setOption('MultiPV', multiPV);
    }
    this.send(`position fen ${fen}`);

    const goParts = ['go'];
    if (typeof depth === 'number') {
      goParts.push(`depth ${depth}`);
    }
    if (typeof movetime === 'number') {
      goParts.push(`movetime ${movetime}`);
    }
    // If neither is given, fall back to an infinite search (caller must stop()).
    if (goParts.length === 1) {
      goParts.push('infinite');
    }
    this.send(goParts.join(' '));

    return token;
  }

  /** Terminate the worker and reset state. */
  destroy(): void {
    this.cleanupWorker();
    this.ready = false;
    this.uciOkResolve = null;
    this.readyOkResolve = null;
  }

  private cleanupWorker(): void {
    if (this.worker) {
      this.worker.onmessage = null;
      this.worker.onerror = null;
      try {
        this.worker.terminate();
      } catch {
        /* noop */
      }
      this.worker = null;
    }
  }
}
